// Singleton service to "remember", if script is loaded state

import { HTMLElementType } from 'constants/enums'

// https://stackoverflow.com/questions/30174078/how-to-define-singleton-in-typescript
interface IScripts {
    name: string
    src: string
    loaded: boolean
}

interface IScriptLoaderResult {
    script: string
    loaded: boolean
    status: string
}

type HTMLAttributes = Record<string, string>
export type FormFields =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement

export class ScriptService {
    private scripts: Record<string, IScripts> = {}

    private static _instance: ScriptService
    public static getInstance(): ScriptService {
        return this._instance || (this._instance = new this())
    }

    async load(...scripts: IScripts[]): Promise<IScriptLoaderResult[]> {
        const promises: Promise<IScriptLoaderResult>[] = []
        scripts.forEach((script) => promises.push(this.loadScript(script)))
        return Promise.all(promises)
    }

    async loadScript(
        scriptObj: IScripts,
        attributes?: HTMLAttributes,
        loadInFooter = false
    ): Promise<IScriptLoaderResult> {
        // save scripts
        if (!this.scripts[scriptObj.name]) {
            this.scripts[scriptObj.name] = {
                name: scriptObj.name,
                loaded: scriptObj.loaded,
                src: scriptObj.src
            }
        }

        return new Promise((resolve) => {
            //resolve if already loaded
            if (this.scripts[scriptObj.name].loaded) {
                resolve({
                    script: scriptObj.name,
                    loaded: true,
                    status: 'Already Loaded'
                })
            } else {
                //load script
                const script = document.createElement(HTMLElementType.SCRIPT)
                script.type = 'text/javascript'
                script.id = `${scriptObj.name}`
                script.src = this.scripts[scriptObj.name].src.replace(
                    /^(?:https?:)?(.+)$/,
                    'https:$1'
                )

                if (attributes) {
                    Object.keys(attributes).forEach((key: string) => {
                        script.setAttribute(key, attributes[key])
                    })
                }

                if (!this.scripts[scriptObj.name].loaded) {
                    if (loadInFooter) {
                        document.body.appendChild(script)
                    } else {
                        document.head.appendChild(script)
                    }
                }

                script.onload = (): void => {
                    this.scripts[scriptObj.name].loaded = true
                    resolve({
                        script: scriptObj.name,
                        loaded: true,
                        status: 'Loaded'
                    })
                }
                script.onerror = (): void =>
                    resolve({
                        script: scriptObj.name,
                        loaded: false,
                        status: 'Error'
                    })
            }
        })
    }
}
